import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  Sparkles,
  LucideIcon,
  UserPlus,
  Users,
  PlusCircle,
  Bot,
  Calendar,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { toolsList } from "@packages/utils";
import DashboardOnboarding from "./DashboardOnboarding";
import { Button } from "@core/button";
import { CreateClassroomModal } from "../../students/components/CreateClassroomModal";
import AddStudentModal from "../../students/components/AddStudentModal";
import { Text } from "../../../core";
import { trackEvent } from "@packages/hooks/useAnalytics";
import { useAppSelector, useAppDispatch } from "@packages/store/hooks";
import {
  getCurrentTeacher,
  toggleFavoriteTool,
} from "@packages/store/userSlice";
import astroMadlen from "@packages/assets/astromadlen.png";
import RecentlyAddedItem from "./DashboardRecentlyAdded";
import AddFavoritesModal from "./DashboardAddFavoritesModal";
import { FavoriteButton } from "@features/tools/components/favorite-button";

interface ActionItem {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  onClick: () => void;
  color: string;
  textColor: string;
  disabled: boolean;
  isNew?: boolean;
  isFavorite?: boolean;
  canBeFavorite?: boolean;
}

const ActionButton = ({
  index,
  action,
}: {
  index: number;
  action: ActionItem;
}) => {
  const { trackEvent: track } = trackEvent();
  const dispatch = useAppDispatch();

  const handleClick = () => {
    track(
      {
        category: "ContentClick",
        action: "dashboard.quick.action.click",
        label: "Dashboard Quick Action Click",
        nonInteraction: false,
      },
      {
        location: "/dashboard/click",
        element: "click-button",
      },
    );
    action.onClick();
  };

  const toggleFavorite = async () => {
    await dispatch(toggleFavoriteTool(action.id));
    dispatch(getCurrentTeacher());
  };

  return (
    <div key={index} className="relative">
      <Button
        onClick={handleClick}
        disabled={action.disabled}
        size="md"
        rounded="xl"
        variant="outline"
        bgColor="bg-white dark:bg-gray-800"
        className={`${action.id} !whitespace-normal relative group border-gray-200 dark:border-gray-700 
   hover:shadow-lg flex items-center space-x-3 w-full h-18 hover:scale-[1.02]`}
      >
        <div className={`p-3 rounded-lg text-white`}>
          <action.icon size={24} className={action.textColor} />
        </div>
        <div className="flex-1 text-left">
          <Text variant="span">{action.title}</Text>
        </div>
        {action.canBeFavorite && (
          <div>
            <FavoriteButton
              isFavorite={action.isFavorite ?? false}
              onToggleFavorite={toggleFavorite}
            />
          </div>
        )}
      </Button>
    </div>
  );
};

const EmptyStateMessage = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => {
  return (
    <motion.div
      className="col-span-full text-center text-gray-500 py-3 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl flex flex-col items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex flex-row items-center justify-center">
        <img src={astroMadlen} alt="Astro Madlen" className="w-12 h-16" />
        <Text variant="p">{title}</Text>
      </div>
      <Text variant="span" className="text-sm text-gray-400">
        {description}
      </Text>
    </motion.div>
  );
};

// Mobile-aware DashboardOnboarding wrapper
const MobileDashboardOnboarding = ({
  children,
  videoName,
  wrapperClassName,
}: {
  children: React.ReactNode;
  videoName: string;
  wrapperClassName?: string;
}) => {
  // Mobil cihazları algılamak için useEffect ile window width'i kontrol edebiliriz
  // veya CSS media query'leri kullanabiliriz
  return (
    <div className="group relative">
      {/* Desktop için normal DashboardOnboarding */}
      <div className="hidden md:block">
        <DashboardOnboarding
          videoName={videoName}
          wrapperClassName={wrapperClassName}
        >
          {children}
        </DashboardOnboarding>
      </div>

      {/* Mobile için sadece children, onboarding yok */}
      <div className="block md:hidden">{children}</div>
    </div>
  );
};

const DashboardQuickActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedClassUUID, setSelectedClassUUID] = useState<string>();
  const [isCreateClassroomOpen, setIsCreateClassroomOpen] = useState(false);
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false);
  const [showEssayGraderModal, setShowEssayGraderModal] = useState(false);
  const [showAllFavorites, setShowAllFavorites] = useState(false);
  const teacherPreferences = useAppSelector(
    (state) => state.user.teacher?.preferences,
  );

  const favorites = teacherPreferences?.favoriteTools || [];
  const recentlyUsed = teacherPreferences?.lastUsedTools || [];

  const essentialTools = [
    {
      id: "createAssignment",
      title: t("quickActions.createAssignment"),
      description: t("quickActions.createAssignmentDesc"),
      icon: PlusCircle,
      onClick: () => navigate("/assignments"),
      color: "bg-indigo-500",
      textColor: "text-indigo-500",
      disabled: false,
      canBeFavorite: false,
    },
    {
      id: "createClassroom",
      title: t("quickActions.createClassroom"),
      description: t("quickActions.createClassroomDesc"),
      icon: Users,
      onClick: () => setIsCreateClassroomOpen(true),
      color: "bg-blue-500",
      textColor: "text-blue-500",
      disabled: false,
      canBeFavorite: false,
    },
    {
      id: "addStudent",
      title: t("quickActions.addStudent"),
      description: t("quickActions.addStudentDesc"),
      icon: UserPlus,
      onClick: () => setIsAddStudentOpen(true),
      color: "bg-pink-500",
      textColor: "text-pink-500",
      disabled: false,
      canBeFavorite: false,
    },
  ];

  // Function to get all tools in a flat structure
  const getAllTools = () => {
    const allTools: ActionItem[] = [];
    toolsList.forEach((category) => {
      category.tools.forEach((tool) => {
        allTools.push({
          id: tool.id,
          title: t(tool.title),
          description: t(tool.description),
          icon: tool.icon,
          onClick: () => {
            if (tool.id === "essay-grader") {
              setShowEssayGraderModal(true);
            } else {
              navigate(tool.path);
            }
          },
          color: category.colors.dashboardBg,
          textColor: category.colors.iconColor,
          disabled: false,
          isFavorite: favorites.includes(tool.id),
          isNew: [
            "ai-supported-tasks",
            "blooms-taxonomy-activity",
            "ai-assignment-designer",
          ].includes(tool.id),
          canBeFavorite: true,
        });
      });
    });
    return allTools;
  };

  const allTools = useMemo(() => getAllTools(), [favorites, recentlyUsed]);

  // Update to show the most recent tools (last 4 items) first
  const recentlyUsedTools = useMemo(() => {
    if (recentlyUsed.length === 0) {
      return [];
    }

    // Create a copy of recentlyUsed array and reverse it to get most recent first
    const mostRecentFirst = [...recentlyUsed].reverse();
    // Get the last 4 used tools (most recent)
    const recentToolIds = mostRecentFirst.slice(0, 4);
    // Map them to their corresponding tool objects, preserving the order of recency
    return recentToolIds
      .map((id) => allTools.find((tool) => tool.id === id))
      .filter(Boolean) as ActionItem[];
  }, [allTools, recentlyUsed]);

  const favoriteTools =
    allTools.filter((tool) => favorites.includes(tool.id)) || [];

  const displayedFavorites = showAllFavorites
    ? favoriteTools
    : favoriteTools.slice(0, 4);

  const recentlyAddedTools = allTools.filter((tool) => tool.isNew).slice(0, 4);

  // Most Used Tools Section (custom-ordered list placed above Favorites)
  const mostUsedTools = useMemo(() => {
    const desiredOrder = [
      "interactive-material",
      "podcasts",
      "maarif-daily-plan",
      "ai-resistant-homework-idea",
      "images",
      "rubric",
      "essay-grader",
      "assistant",
      "createAssignment",
    ];

    const items: ActionItem[] = [];

    desiredOrder.forEach((id) => {
      // First try to find in allTools (generated from toolsList)
      const found = allTools.find((tool) => tool.id === id);
      if (found) {
        items.push(found);
        return;
      }

      // Add special entries that are not part of toolsList
      if (id === "assistant") {
        items.push({
          id: "assistant",
          title: t("sidebar.menu.assistant.title"),
          description: t("sidebar.menu.assistant.title"),
          icon: Bot,
          onClick: () => navigate("/assistants/new/empty"),
          color: "bg-violet-500",
          textColor: "text-violet-500",
          disabled: false,
          canBeFavorite: false,
        });
        return;
      }

      if (id === "maarif-daily-plan") {
        items.push({
          id: "maarif-daily-plan",
          title: t("sidebar.menu.lessonPlanningTools.maarifDailyPlan"),
          description: t("dailyLessonPlan.maarifDescription"),
          icon: Calendar,
          onClick: () => navigate("/lesson-planning-tools/maarif-daily-plan"),
          color: "bg-emerald-500",
          textColor: "text-emerald-600",
          disabled: false,
          canBeFavorite: false,
        });
        return;
      }

      if (id === "createAssignment") {
        const assignmentTool = essentialTools.find(
          (t) => t.id === "createAssignment",
        );
        if (assignmentTool) {
          items.push(assignmentTool);
        }
      }
    });

    return items;
  }, [allTools, essentialTools, navigate, t]);

  return (
    <div className="space-y-8">
      {/* Most Used Tools Section */}
      <div className="dashboard-section">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 mb-4">
          <Text variant="h2" className="text-sm sm:text-xl">
            {t("quickActions.mostUsedTools")}
          </Text>
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
          layout
        >
          <AnimatePresence mode="popLayout">
            {mostUsedTools.map((tool, index) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
                layout
                transition={{
                  opacity: { duration: 0.2 },
                  layout: { duration: 0.3, type: "spring", stiffness: 100 },
                }}
              >
                <MobileDashboardOnboarding videoName={tool.id}>
                  <ActionButton index={index} action={tool} />
                </MobileDashboardOnboarding>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
      {/* Favorites Section */}
      <div className="dashboard-section">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 mb-4">
          <Text variant="h2" className="text-sm sm:text-xl">
            {t("quickActions.favorites")}
          </Text>
          <div className="flex items-center gap-2">
            {favoriteTools.length > 4 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllFavorites(!showAllFavorites)}
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-xs sm:text-sm"
              >
                <span>
                  {showAllFavorites
                    ? t("common.showLess")
                    : t("common.showMore")}
                </span>
                <ChevronRight
                  size={16}
                  className={showAllFavorites ? "rotate-90" : ""}
                />
              </Button>
            )}
            <AddFavoritesModal />
          </div>
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
          layout
        >
          <AnimatePresence mode="popLayout">
            {displayedFavorites.length > 0 ? (
              displayedFavorites.map((tool, index) => (
                <motion.div
                  key={tool.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{
                    opacity: 0,
                    scale: 0.8,
                    transition: { duration: 0.2 },
                  }}
                  layout
                  transition={{
                    opacity: { duration: 0.2 },
                    layout: { duration: 0.3, type: "spring", stiffness: 100 },
                  }}
                >
                  <MobileDashboardOnboarding videoName={tool.id}>
                    <ActionButton index={index} action={tool} />
                  </MobileDashboardOnboarding>
                </motion.div>
              ))
            ) : (
              <EmptyStateMessage
                title="quickActions.noFavorites"
                description="quickActions.addFavoritesHint"
              />
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Recently Added Section */}
      <div className="dashboard-section relative">
        {/* Decorative Corner Element */}
        <div className="absolute -top-2 -right-2 w-16 h-16 bg-orange-500/10 rounded-full blur-xl"></div>

        <div className="flex md:items-center items-start md:gap-0 gap-2 justify-between md:flex-row flex-col md:mb-4 mb-6">
          <Text variant="h2" className="flex items-center text-sm sm:text-xl">
            <span className="relative">
              {t("quickActions.recentlyAdded")}
              <span className="absolute -right-6 top-1/2 transform -translate-y-1/2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                </span>
              </span>
            </span>
          </Text>

          <div className="flex items-center">
            <Sparkles size={16} className="mr-1.5 text-orange-500" />
            <Text variant="span" status="secondary">
              {t("quickActions.freshFeaturesToExplore")}
            </Text>
          </div>
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3  gap-4 auto-rows-fr"
          layout
        >
          <AnimatePresence mode="popLayout">
            {recentlyAddedTools.map((tool) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
                layout
                transition={{
                  opacity: { duration: 0.2 },
                  layout: { duration: 0.3, type: "spring", stiffness: 100 },
                }}
              >
                <MobileDashboardOnboarding
                  videoName={tool.id}
                  wrapperClassName="h-full w-full"
                >
                  <RecentlyAddedItem action={tool} />
                </MobileDashboardOnboarding>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <CreateClassroomModal
        show={isCreateClassroomOpen}
        onClose={() => setIsCreateClassroomOpen(false)}
        onSubmit={(newClassroom) => {
          setSelectedClassUUID(newClassroom.uuid);
          setTimeout(() => {
            setIsCreateClassroomOpen(false);
            setIsAddStudentOpen(true);
          }, 50);
        }}
      />

      <AddStudentModal
        show={isAddStudentOpen}
        onClose={() => {
          setIsAddStudentOpen(false);
          setSelectedClassUUID(undefined);
        }}
        uuid={selectedClassUUID}
      />
    </div>
  );
};

export default DashboardQuickActions;
